import React, { useState } from "react";
import { useData } from "@/context/DataContext";
import { PageTitle, Fab, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Pencil, Trash2, Plus, Palette, ImagePlus, X } from "lucide-react";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";

const CATEGORIES = ["Busana", "Catering", "Dekorasi", "Fotografi", "Venue", "MUA", "Suvenir", "Musik", "Lainnya"];

export default function Moodboard() {
  const { data, create, update, remove } = useData();
  const items = (data.moodboard || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ kategori: "Dekorasi", nama_bagian: "", photos: [] });

  const openNew = () => { setEditing(null); setForm({ kategori: "Dekorasi", nama_bagian: "", photos: [] }); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      kategori: it.kategori || "Lainnya",
      nama_bagian: it.nama_bagian || "",
      photos: Array.isArray(it.photos) ? it.photos.slice(0, 2) : [],
    });
    setOpen(true);
  };

  const onUpload = async (file) => {
    if (!file) return;
    if (form.photos.length >= 2) { toast.error("Maksimal 2 foto per entri"); return; }
    try {
      const b64 = await resizeImage(file, 1200);
      setForm((f) => ({ ...f, photos: [...f.photos, b64] }));
    } catch { toast.error("Gagal memproses foto"); }
  };
  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.nama_bagian.trim()) { toast.error("Nama bagian wajib diisi"); return; }
    const payload = { kategori: form.kategori, nama_bagian: form.nama_bagian.trim(), photos: form.photos };
    try {
      if (editing) { await update("moodboard", editing.id, payload); toast.success("Entri diperbarui"); }
      else { await create("moodboard", payload); toast.success("Entri ditambahkan"); }
      setOpen(false);
    } catch { toast.error("Gagal menyimpan"); }
  };

  return (
    <div>
      <PageTitle title="Moodboard" subtitle="Kumpulkan referensi pernikahan impian per kategori" icon={Palette} />

      {items.length === 0 ? (
        <EmptyState text="Belum ada entri moodboard. Klik tombol + di kanan bawah untuk mulai." />
      ) : (
        <div className="-mx-4 overflow-x-auto px-4 pb-4 kh-scroll" data-testid="moodboard-scroll">
          <div className="flex gap-4">
            {items.map((it) => (
              <Card key={it.id} data-testid={`moodboard-card-${it.id}`} className="flex w-[280px] shrink-0 flex-col overflow-hidden">
                <div className="grid grid-cols-2 gap-0.5 bg-muted">
                  {[0, 1].map((idx) => (
                    <div key={idx} className="aspect-square overflow-hidden bg-muted">
                      {it.photos?.[idx] ? (
                        <img src={it.photos[idx]} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-muted-foreground">
                          <ImagePlus className="h-6 w-6 opacity-40" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <p className="text-[10px] uppercase tracking-widest text-primary">{it.kategori}</p>
                  <h3 className="mt-1 font-heading text-lg font-semibold">{it.nama_bagian}</h3>
                  <div className="mt-auto flex gap-1.5 pt-3">
                    <Button data-testid={`moodboard-edit-${it.id}`} onClick={() => openEdit(it)} size="sm" variant="outline" className="flex-1 gap-1">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button data-testid={`moodboard-delete-${it.id}`} onClick={() => { if (window.confirm("Hapus entri ini?")) remove("moodboard", it.id); }} size="sm" variant="outline" className="gap-1 text-red-600 hover:text-red-700">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Fab testId="fab-moodboard" onClick={openNew} icon={Plus} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="moodboard-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit Moodboard" : "Tambah Moodboard"}</DialogTitle>
            <DialogDescription className="sr-only">Formulir moodboard</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Kategori</Label>
              <select data-testid="moodboard-kategori" className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={form.kategori} onChange={(e) => setForm((f) => ({ ...f, kategori: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nama Bagian *</Label>
              <Input data-testid="moodboard-nama"
                className="mt-1.5" value={form.nama_bagian}
                onChange={(e) => setForm((f) => ({ ...f, nama_bagian: e.target.value }))}
                placeholder="mis. Backdrop Rustic, Menu Prasmanan…" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Foto Referensi (maks 2)</Label>
              <div className="mt-2 flex gap-2">
                {form.photos.map((src, idx) => (
                  <div key={idx} data-testid={`moodboard-photo-${idx}`} className="group relative h-24 w-24 overflow-hidden rounded-md border border-border">
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    <button onClick={() => removePhoto(idx)} className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white opacity-0 group-hover:opacity-100">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {form.photos.length < 2 && (
                  <label data-testid="moodboard-add-photo" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary">
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-[10px]">Tambah</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                )}
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">Foto otomatis dikompres ke 1200px.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="moodboard-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
